import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";
import { EMERGENCY_DRUGS, SYRINGE_VOLUMES, calculateInfusion } from "@/lib/emergency_drugs";
import { cn } from "@/lib/utils";

export default function EmergencyDrugs() {
  const t = useT();

  const [searchParams] = useSearchParams();
  const initialDrugId = useMemo(() => {
    const q = searchParams.get("drug");
    return q && EMERGENCY_DRUGS.some((d) => d.id === q) ? q : EMERGENCY_DRUGS[0].id;
  }, [searchParams]);
  const [drugId, setDrugId] = useState(initialDrugId);
  const drug = EMERGENCY_DRUGS.find((d) => d.id === drugId);

  const [weight, setWeight] = useState("");
  const [strength, setStrength] = useState(String(drug.defaultStrengthMg));
  const [volume, setVolume] = useState("50");
  const [dose, setDose] = useState(String(drug.defaultDose));
  // For drugs with multiple dose-unit strategies (e.g. Nicardipine)
  const [unitIdx, setUnitIdx] = useState(0);

  // Active dose-unit config for this drug
  const activeUnit =
    drug.doseUnits && drug.doseUnits[unitIdx]
      ? drug.doseUnits[unitIdx]
      : {
          unit: drug.doseUnit,
          formula: drug.formula,
          default: drug.defaultDose,
          range: drug.doseRange,
          step: drug.doseStep,
        };

  // when drug changes, reset its drug-specific defaults
  React.useEffect(() => {
    setStrength(String(drug.defaultStrengthMg));
    // pick the drug's first dose-unit (or single fixed)
    const first = drug.doseUnits ? drug.doseUnits[0] : null;
    setUnitIdx(0);
    setDose(String(first ? first.default : drug.defaultDose));
  }, [drug]);

  // when the user toggles unit on a multi-unit drug, reset dose to that unit's default
  React.useEffect(() => {
    if (drug.doseUnits) {
      setDose(String(drug.doseUnits[unitIdx].default));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitIdx]);

  const calc = useMemo(
    () =>
      calculateInfusion({
        weight,
        strengthMg: strength,
        volumeCc: volume,
        dose,
        formula: activeUnit.formula,
      }),
    [weight, strength, volume, dose, activeUnit.formula]
  );

  return (
    <div data-testid="page-emergency-drugs">
      <PageHeader
        title={t("emerg.title")}
        subtitle={t("emerg.subtitle")}
        formula={`${t("emerg.formula_lead")}:  rate (cc/hr) = (dose × weight × 60) ÷ concentration (mcg/cc)`}
      />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <div className="space-y-6">
          <Field label={t("emerg.drug")} testid="drug">
            <select
              value={drugId}
              onChange={(e) => setDrugId(e.target.value)}
              data-testid="drug-select"
              className="w-full bg-white border border-stone-200 rounded-sm px-3 py-3 text-base font-sans hover:border-stone-300 focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/30 transition-colors duration-100 min-h-[44px]"
            >
              {EMERGENCY_DRUGS.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>

          <div
            data-testid="diluent-badge"
            className={cn(
              "flex items-start gap-2 border rounded-sm px-3 py-2.5 text-xs leading-relaxed",
              drug.diluentWarn
                ? "border-amber-700/40 bg-amber-50 text-amber-900"
                : "border-sky-700/30 bg-sky-700/5 text-stone-700"
            )}
          >
            <span
              className={cn(
                "font-mono text-[10px] tracking-[0.12em] uppercase font-semibold px-1.5 py-0.5 rounded-sm shrink-0 mt-0.5",
                drug.diluentWarn
                  ? "bg-amber-700/10 text-amber-900"
                  : "bg-sky-700/10 text-sky-700"
              )}
            >
              {t("emerg.diluent")}
            </span>
            <span data-testid="diluent-text">{drug.diluent}</span>
          </div>

          <Field label={t("emerg.weight")} unit="kg" testid="weight">
            <NumberInput
              value={weight}
              onChange={setWeight}
              placeholder="e.g. 70"
              min={0}
              testid="weight-input"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t("emerg.strength")} unit="mg" testid="strength">
              <NumberInput
                value={strength}
                onChange={setStrength}
                placeholder={String(drug.defaultStrengthMg)}
                min={0}
                testid="strength-input"
              />
            </Field>
            <Field label={t("emerg.volume")} testid="volume">
              <div
                className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full"
                role="group"
                data-testid="volume-group"
              >
                {SYRINGE_VOLUMES.map((v) => {
                  const active = String(volume) === String(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      data-testid={`volume-${v}`}
                      onClick={() => setVolume(String(v))}
                      className={cn(
                        "flex-1 px-3 text-sm transition-colors duration-100 min-h-[44px] font-mono",
                        active ? "bg-sky-700 text-white" : "bg-white text-stone-700 hover:bg-stone-50"
                      )}
                      aria-pressed={active}
                    >
                      {v} cc
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          {drug.doseUnits && (
            <Field label={t("emerg.dose_unit")} testid="dose-unit">
              <div
                className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full"
                role="group"
                data-testid="dose-unit-group"
              >
                {drug.doseUnits.map((du, i) => {
                  const active = unitIdx === i;
                  return (
                    <button
                      key={du.unit}
                      type="button"
                      data-testid={`dose-unit-${i}`}
                      onClick={() => setUnitIdx(i)}
                      className={cn(
                        "flex-1 px-3 text-sm transition-colors duration-100 min-h-[44px] font-mono",
                        active ? "bg-sky-700 text-white" : "bg-white text-stone-700 hover:bg-stone-50"
                      )}
                      aria-pressed={active}
                    >
                      {du.unit}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <Field
            label={`${t("emerg.target_dose")} (${activeUnit.unit})`}
            unit={activeUnit.unit}
            testid="dose"
            hint={`${t("emerg.typical_range")}: ${activeUnit.range[0]}–${activeUnit.range[1]} ${activeUnit.unit}`}
          >
            <NumberInput
              value={dose}
              onChange={setDose}
              placeholder={String(activeUnit.default)}
              min={0}
              step={activeUnit.step}
              testid="dose-input"
            />
          </Field>

          <Note>{drug.notes}</Note>
        </div>

        <div className="space-y-4">
          <Result
            label={t("emerg.result.pump_rate")}
            value={calc ? fmt(calc.rateCcPerHr, 2) : "—"}
            unit="cc / hr"
            big
            testid="result-rate"
          />
          <Result
            label={t("emerg.result.conc_mcg")}
            value={calc ? fmt(calc.concMcgPerCc, 1) : "—"}
            unit="mcg / cc"
            testid="result-conc-mcg"
          />
          <Result
            label={t("emerg.result.conc_mg")}
            value={calc ? fmt(calc.concMgPerCc, 2) : "—"}
            unit="mg / cc"
            testid="result-conc-mg"
          />
          <FormulaBox testid="breakdown-box">
            {calc
              ? `Concentration = ${strength} mg ÷ ${volume} cc = ${fmt(
                  calc.concMgPerCc,
                  2
                )} mg/cc (${fmt(calc.concMcgPerCc, 0)} mcg/cc)\nTarget = ${dose} ${activeUnit.unit}${
                  activeUnit.formula.includes("PerKg") ? `   @ weight = ${weight || "—"} kg` : ""
                }\nPump rate = ${fmt(calc.rateCcPerHr, 2)} cc/hr`
              : t("emerg.empty")}
          </FormulaBox>
        </div>
      </div>
    </div>
  );
}
